import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Save, User, MapPin, Truck, ListChecks, Euro, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getCsrfToken } from '../../lib/api';

const API = ''; // use relative URLs for production-safe API calls

const SERVICE_LABELS = {
  transport: 'Transport',
  loading: 'Be- und Entladung',
  furniture_assembly: 'Möbelmontage',
  kitchen_assembly: 'Küchenmontage',
  packing_service: 'Verpackungsservice',
  decluttering: 'Entrümpelung',
  storage: 'Einlagerung',
};

const VEHICLE_OPTIONS = ['Transporter (3,5 t)', 'LKW 7,5 t', 'LKW 12 t', 'LKW 18 t', 'Sonstiges'];

const emptyForm = {
  contract_type: 'umzug',
  customer_id: null,
  customer: { first_name: '', last_name: '', phone: '', email: '', old_address: '', new_address: '' },
  job: { move_date: '', move_time: '', staff_count: 2, vehicle_size: '', floors: '', elevator: false, distance_walk: '', no_parking_zone: false },
  services: { transport: true, loading: false, furniture_assembly: false, kitchen_assembly: false, packing_service: false, decluttering: false, storage: false },
  pricing: { pricing_mode: 'fixed', fixed_price: 0, hourly_rate: 0, estimated_hours: 0, additional_costs: 0, deposit: 0, notes: '' },
  custom_clauses: '',
};

export default function AdminContractForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [previewNumber, setPreviewNumber] = useState('VTR-…');
  const [archiveCount, setArchiveCount] = useState(0);

  useEffect(() => {
    fetch(`/api/admin/contracts`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(d => setArchiveCount(Array.isArray(d) ? d.length : 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/api/customers`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setCustomers)
      .catch(() => {});

    if (!isEdit) {
      fetch(`${API}/api/admin/contracts/next-number`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setPreviewNumber(d.number));
    } else {
      fetch(`${API}/api/admin/contracts/${id}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d) return;
          setForm({
            contract_type: d.contract_type || 'umzug',
            customer_id: d.customer_id,
            customer: { ...emptyForm.customer, ...(d.customer || {}) },
            job: { ...emptyForm.job, ...(d.job || {}) },
            services: { ...emptyForm.services, ...(d.services || {}) },
            pricing: { ...emptyForm.pricing, ...(d.pricing || {}) },
            custom_clauses: d.custom_clauses || '',
          });
          setPreviewNumber(d.contract_number);
        });
    }
  }, [id, isEdit]);

  const update = (path, value) => {
    setForm(f => {
      const copy = { ...f };
      const keys = path.split('.');
      let target = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        target[keys[i]] = { ...target[keys[i]] };
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const onCustomerSelect = (cid) => {
    const c = customers.find(x => x.id === cid);
    if (!c) return;
    update('customer_id', cid);
    update('customer.first_name', c.first_name || '');
    update('customer.last_name', c.last_name || '');
    update('customer.phone', c.phone || '');
    update('customer.email', c.email || '');
    update('customer.old_address', `${c.street || ''}, ${c.postal_code || ''} ${c.city || ''}`.trim().replace(/^,\s*/, ''));
  };

  const computedTotal = (() => {
    const p = form.pricing;
    const base = p.pricing_mode === 'hourly' ? (Number(p.hourly_rate) * Number(p.estimated_hours)) : Number(p.fixed_price);
    const total = base + Number(p.additional_costs || 0);
    return { base, total, remaining: total - Number(p.deposit || 0) };
  })();

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.customer.first_name || !form.customer.last_name) return toast.error('Vor- und Nachname sind Pflicht');
    setSaving(true);
    try {
      const url = isEdit ? `${API}/api/admin/contracts/${id}` : `${API}/api/admin/contracts`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() || '' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Speichern fehlgeschlagen');
      }
      const data = await res.json();
      toast.success(isEdit ? 'Vertrag aktualisiert' : 'Vertrag erstellt');
      navigate(`/admin/vertraege/${data.id || id}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="contract-form">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate('/admin/vertraege')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <div>
            <h1 className="text-2xl font-barlow font-bold text-[#1A1A1A]">{isEdit ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}</h1>
            <p className="text-sm text-gray-500">Vertragsnr.: <span className="font-mono text-[#9B1B30]">{previewNumber}</span></p>
          </div>
        </div>
        <Button type="submit" disabled={saving} className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white" data-testid="contract-save-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichern…' : 'Speichern'}
        </Button>
      </div>

      {/* Tabs (Office-Style) */}
      {!isEdit && (
        <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="contract-form-tabs">
          <span
            className="px-4 py-1.5 rounded-md text-sm font-medium bg-white text-[#9B1B30] shadow-sm"
            data-testid="contract-form-tab-create"
          >
            Neuer Vertrag
          </span>
          <button
            type="button"
            onClick={() => navigate('/admin/vertraege')}
            className="px-4 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-white/60 transition-colors"
            data-testid="contract-form-tab-archive"
          >
            Archiv ({archiveCount})
          </button>
        </div>
      )}

      {/* Section: Customer */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-[#9B1B30]" />
            <h2 className="font-bold text-lg">Kundendaten</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Aus CRM-Kunde übernehmen (optional)</Label>
              <Select value={form.customer_id || ''} onValueChange={onCustomerSelect}>
                <SelectTrigger><SelectValue placeholder="Kunde auswählen…" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.customer_number ? `(${c.customer_number})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Vorname *</Label><Input value={form.customer.first_name} onChange={e => update('customer.first_name', e.target.value)} data-testid="contract-firstname" /></div>
            <div><Label>Nachname *</Label><Input value={form.customer.last_name} onChange={e => update('customer.last_name', e.target.value)} data-testid="contract-lastname" /></div>
            <div><Label>Telefon</Label><Input value={form.customer.phone} onChange={e => update('customer.phone', e.target.value)} /></div>
            <div><Label>E-Mail</Label><Input type="email" value={form.customer.email} onChange={e => update('customer.email', e.target.value)} /></div>
            <div className="md:col-span-2"><Label><MapPin className="w-3 h-3 inline mr-1" /> Alte Adresse</Label><Input value={form.customer.old_address} onChange={e => update('customer.old_address', e.target.value)} placeholder="Straße, PLZ Ort" /></div>
            <div className="md:col-span-2"><Label><MapPin className="w-3 h-3 inline mr-1" /> Neue Adresse</Label><Input value={form.customer.new_address} onChange={e => update('customer.new_address', e.target.value)} placeholder="Straße, PLZ Ort" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Section: Job */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-[#2E2A88]" />
            <h2 className="font-bold text-lg">Auftragsdaten</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Umzugsdatum</Label><Input type="date" value={form.job.move_date} onChange={e => update('job.move_date', e.target.value)} data-testid="contract-movedate" /></div>
            <div><Label>Uhrzeit</Label><Input type="time" value={form.job.move_time} onChange={e => update('job.move_time', e.target.value)} /></div>
            <div><Label>Anzahl Mitarbeiter</Label><Input type="number" min="1" value={form.job.staff_count} onChange={e => update('job.staff_count', parseInt(e.target.value || 0))} /></div>
            <div>
              <Label>Fahrzeuggröße</Label>
              <Select value={form.job.vehicle_size} onValueChange={v => update('job.vehicle_size', v)}>
                <SelectTrigger><SelectValue placeholder="Wählen…" /></SelectTrigger>
                <SelectContent>{VEHICLE_OPTIONS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Stockwerke</Label><Input value={form.job.floors} onChange={e => update('job.floors', e.target.value)} placeholder="z.B. 3. OG → EG" /></div>
            <div><Label>Laufweg</Label><Input value={form.job.distance_walk} onChange={e => update('job.distance_walk', e.target.value)} placeholder="z.B. 25 m" /></div>
            <div className="flex items-center gap-2 pt-6">
              <Checkbox id="elevator" checked={form.job.elevator} onCheckedChange={v => update('job.elevator', !!v)} />
              <Label htmlFor="elevator" className="cursor-pointer">Aufzug vorhanden</Label>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Checkbox id="parking" checked={form.job.no_parking_zone} onCheckedChange={v => update('job.no_parking_zone', !!v)} />
              <Label htmlFor="parking" className="cursor-pointer">Halteverbotszone benötigt</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section: Services */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-5 h-5 text-emerald-600" />
            <h2 className="font-bold text-lg">Leistungen</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(SERVICE_LABELS).map(([k, l]) => (
              <label key={k} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${form.services[k] ? 'border-[#9B1B30] bg-[#9B1B30]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                <Checkbox checked={form.services[k]} onCheckedChange={v => update(`services.${k}`, !!v)} data-testid={`service-${k}`} />
                <span className="font-medium text-sm">{l}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Section: Pricing */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Euro className="w-5 h-5 text-[#9B1B30]" />
            <h2 className="font-bold text-lg">Preise & Zahlungen</h2>
          </div>
          <div className="flex gap-2 mb-3">
            <button type="button" onClick={() => update('pricing.pricing_mode', 'fixed')} className={`px-4 py-2 rounded-lg border-2 font-medium text-sm ${form.pricing.pricing_mode === 'fixed' ? 'border-[#9B1B30] bg-[#9B1B30] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-testid="pricing-mode-fixed">Festpreis</button>
            <button type="button" onClick={() => update('pricing.pricing_mode', 'hourly')} className={`px-4 py-2 rounded-lg border-2 font-medium text-sm ${form.pricing.pricing_mode === 'hourly' ? 'border-[#9B1B30] bg-[#9B1B30] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-testid="pricing-mode-hourly">Stundenpreis</button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {form.pricing.pricing_mode === 'fixed' ? (
              <div><Label>Festpreis (€)</Label><Input type="number" step="0.01" value={form.pricing.fixed_price} onChange={e => update('pricing.fixed_price', parseFloat(e.target.value || 0))} data-testid="contract-fixed-price" /></div>
            ) : (
              <>
                <div><Label>Stundensatz (€)</Label><Input type="number" step="0.01" value={form.pricing.hourly_rate} onChange={e => update('pricing.hourly_rate', parseFloat(e.target.value || 0))} data-testid="contract-hourly-rate" /></div>
                <div><Label>Geschätzte Stunden</Label><Input type="number" step="0.5" value={form.pricing.estimated_hours} onChange={e => update('pricing.estimated_hours', parseFloat(e.target.value || 0))} data-testid="contract-estimated-hours" /></div>
              </>
            )}
            <div><Label>Zusatzkosten (€)</Label><Input type="number" step="0.01" value={form.pricing.additional_costs} onChange={e => update('pricing.additional_costs', parseFloat(e.target.value || 0))} data-testid="contract-additional-costs" /></div>
            <div><Label>Anzahlung (€)</Label><Input type="number" step="0.01" value={form.pricing.deposit} onChange={e => update('pricing.deposit', parseFloat(e.target.value || 0))} data-testid="contract-deposit" /></div>
            <div className="md:col-span-2"><Label>Preisbemerkung</Label><Textarea rows={2} value={form.pricing.notes} onChange={e => update('pricing.notes', e.target.value)} /></div>
          </div>
          <div className="bg-gradient-to-br from-[#9B1B30]/5 to-[#2E2A88]/5 rounded-lg p-4 grid grid-cols-3 gap-2 text-center">
            <div><p className="text-xs text-gray-500">Grundpreis</p><p className="text-lg font-bold">{computedTotal.base.toFixed(2)} €</p></div>
            <div><p className="text-xs text-gray-500">Gesamtbrutto</p><p className="text-lg font-bold text-[#9B1B30]">{computedTotal.total.toFixed(2)} €</p></div>
            <div><p className="text-xs text-gray-500">Restzahlung</p><p className="text-lg font-bold">{computedTotal.remaining.toFixed(2)} €</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Section: Custom Clauses */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-gray-600" />
            <h2 className="font-bold text-lg">Ergänzende Vereinbarungen (optional)</h2>
          </div>
          <Textarea rows={4} value={form.custom_clauses} onChange={e => update('custom_clauses', e.target.value)} placeholder="Individuelle Zusätze – Standard-Haftungs- & Stornierungsklauseln werden automatisch ergänzt." />
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r text-sm text-amber-900">
            <strong>Automatisch ergänzt:</strong> 4 Haftungsklauseln (Bargeld, Verpackung, Vorschäden, Schadensmeldung) und 4 Stornierungsstufen (14 Tage / 7 Tage / 48 h / Umzugstag).
          </div>
        </CardContent>
      </Card>

      {/* Footer Actions */}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={() => navigate('/admin/vertraege')}>Abbrechen</Button>
        <Button type="submit" disabled={saving} className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichern…' : 'Speichern'}
        </Button>
      </div>
    </form>
  );
}
